import { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Loader2, CheckCircle2 } from "lucide-react";
import { useGetFirmApplicationDetailsQuery } from "../api/firmApi";

/**
 * Landing page after the OnePay redirect — for BOTH firm charges, the
 * registration fee and the donation. Polls the application until the
 * verification job moves it on, then bounces to the details page.
 */
export default function FirmPaymentProcessingPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: app } = useGetFirmApplicationDetailsQuery(id!, {
    skip: !id,
    pollingInterval: 4000,
  });

  // Anything past "waiting to be paid for" means the money landed. Listed
  // rather than "not pending_payment", so a draft never reads as settled.
  const SETTLED = [
    "awaiting_endorsement", "under_review", "with_committee", "returned", "rejected",
    "ready_for_interview", "pending_donation", "donated", "approved",
  ];
  const settled = app && SETTLED.includes(app.status);
  const donationSettled = app?.status === "donated";

  useEffect(() => {
    if (settled) {
      const t = setTimeout(() => navigate(`/firm-applications/${id}`), 1500);
      return () => clearTimeout(t);
    }
  }, [settled, id, navigate]);

  return (
    <div className="min-h-[60vh] flex items-center justify-center p-6">
      <div className="bg-white border rounded-2xl shadow-sm p-8 max-w-md w-full text-center space-y-4">
        {settled ? (
          <>
            <div className="w-16 h-16 rounded-full bg-green-50 border border-green-200 mx-auto flex items-center justify-center">
              <CheckCircle2 className="w-8 h-8 text-green-500" />
            </div>
            <h2 className="text-xl font-bold text-gray-900">Payment confirmed</h2>
            <p className="text-sm text-gray-500">
              {donationSettled
                ? "Your donation has been received. The secretariat will issue your firm number."
                : "Your referees will be asked to confirm, and your chapter needs to endorse the application."}
            </p>
          </>
        ) : (
          <>
            <Loader2 className="w-10 h-10 animate-spin text-indigo-500 mx-auto" />
            <h2 className="text-xl font-bold text-gray-900">Confirming your payment…</h2>
            <p className="text-sm text-gray-500">
              This can take a moment. Please don't close this page — it will update automatically.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
